import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Calendar, BarChart3, Plus, Trash2, Upload, Loader, Download,
  Sun, Moon, ChevronDown, ChevronRight,
  TrendingUp, TrendingDown, Wallet, LogOut, Send, Camera,
} from "lucide-react";
import { signOut } from "firebase/auth";
import { auth } from "./firebase";
import { useAuth } from "./AuthContext";
import ProfilePage from "./ProfilePage";
import ContactPage from "./ContactPage";

// ===== 定数・ユーティリティ =====

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
if (!BACKEND_URL) throw new Error("REACT_APP_API_BASE_URL is not set");

const pad2 = (n) => String(n).padStart(2, "0");
const ymKey = (year, month) => `${year}-${pad2(month)}`;

const getYearMonthFromDate = (dateStr) => {
  const s = String(dateStr);
  return {
    year: parseInt(s.slice(0, 4), 10),
    month: parseInt(s.slice(5, 7), 10),
    monthKey: s.slice(0, 7),
  };
};

// ===== カテゴリ定義 =====

const categories = {
  sales:        { label: "売上(収入)" },
  household:    { label: "家事消費" },
  otherIncome:  { label: "その他の収入" },
  purchases:    { label: "仕入金額" },
  salary:       { label: "給料賃金" },
  outsourcing:  { label: "外注工賃" },
  rent:         { label: "地代家賃" },
  utilities:    { label: "水道光熱費" },
  communication:{ label: "通信費" },
  travel:       { label: "旅費交通費" },
  advertising:  { label: "広告宣伝費" },
  insurance:    { label: "損害保険料" },
  supplies:     { label: "消耗品費" },
  paymentFee:   { label: "支払手数料" },
  training:     { label: "研修費" },
  entertainment: { label: "接待交際費" },
  other:        { label: "雑費" },
};

const JP_TO_KEY = Object.fromEntries(Object.entries(categories).map(([k, v]) => [v.label, k]));
const KEY_TO_JP = Object.fromEntries(Object.entries(categories).map(([k, v]) => [k, v.label]));

const CAT_ALIAS = {
  "売上": "sales", "売上(収入)": "sales", "収入": "sales",
  "家事消費": "household", "世帯": "household",
  "その他の収入": "otherIncome",
  "消耗品": "supplies", "消耗品費": "supplies",
};

const normalizeCatToKey = (raw) => {
  const c = String(raw ?? "").trim();
  if (categories[c]) return c;
  return JP_TO_KEY[c] || CAT_ALIAS[c] || c;
};

const INCOME_KEYS = new Set(["sales", "household", "otherIncome"]);
const isIncomeCategory = (cat) => INCOME_KEYS.has(normalizeCatToKey(cat));

const calcIncomeExpense = (txns) => {
  let income = 0, expense = 0;
  for (const t of txns) {
    const amt = Number(t.amount || 0);
    if (isIncomeCategory(t.category)) income += amt; else expense += amt;
  }
  return { income, expense, count: txns.length };
};

const normalizeRow = (r) => ({
  id: r.id ?? r.ID ?? r._id,
  date: r.date ?? r.日付,
  category: normalizeCatToKey(r.category ?? r.カテゴリ ?? ""),
  amount: Number(r.amount ?? r.金額 ?? 0),
  memo: r.memo ?? r.メモ ?? "",
});

const TAX_ITEMS = [
  { key: "sales", label: "売上(収入)金額" }, { key: "household", label: "家事消費" },
  { key: "otherIncome", label: "その他の収入" }, { key: "purchases", label: "仕入金額" },
  { key: "salary", label: "給料賃金" }, { key: "outsourcing", label: "外注工賃" },
  { key: "rent", label: "地代家賃" }, { key: "utilities", label: "水道光熱費" },
  { key: "communication", label: "通信費" }, { key: "travel", label: "旅費交通費" },
  { key: "advertising", label: "広告宣伝費" }, { key: "insurance", label: "損害保険料" },
  { key: "supplies", label: "消耗品費" }, { key: "paymentFee", label: "支払手数料" },
  { key: "training", label: "研修費" }, { key: "entertainment", label: "接待交際費" },
  { key: "other", label: "雑費" },
];

const formatYen = (n) => `¥${Number(n || 0).toLocaleString()}`;

// ===== CSV エクスポート =====

/** CSV用にセルの値をエスケープ（カンマ・改行・ダブルクォートを含む場合） */
const csvEscape = (val) => {
  const s = String(val ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/** CSVテキストをBOM付きUTF-8でダウンロード（Excelで文字化けしない） */
const downloadCsv = (filename, csvText) => {
  const bom = "\uFEFF";
  const blob = new Blob([bom + csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ===== メインコンポーネント =====

const AccountingApp = () => {
  const { getToken } = useAuth();

  // 認証トークン付きfetch
  const authFetch = useCallback(async (url, options = {}) => {
    const token = await getToken();
    const headers = { ...(options.headers || {}), Authorization: `Bearer ${token}` };
    return fetch(url, { ...options, headers });
  }, [getToken]);

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(now.getMonth() + 1);
  const [view, setView] = useState("daily");
  const [theme, setTheme] = useState("dark");

  const [transactionsByMonth, setTransactionsByMonth] = useState({});
  const [loadingByMonth, setLoadingByMonth] = useState({});

  const [newTransaction, setNewTransaction] = useState({
    date: new Date().toISOString().split("T")[0],
    category: "sales",
    amount: "",
    description: "",
  });

  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedDate, setExpandedDate] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [annualLoading, setAnnualLoading] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showContact, setShowContact] = useState(false);

  // テーマ適用
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  // ===== API =====
  const fetchMonth = useCallback(async (year, month) => {
    const res = await authFetch(`${BACKEND_URL}/api/transactions?year=${year}&month=${month}`);
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json?.message || "取得に失敗しました");
    return (json.data || []).map(normalizeRow);
  }, [authFetch]);

  const fetchAnnual = useCallback(async (year) => {
    const res = await authFetch(`${BACKEND_URL}/api/transactions/annual?year=${year}`);
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json?.message || "年間取得に失敗しました");
    const byMonth = {};
    for (let m = 1; m <= 12; m++) byMonth[ymKey(year, m)] = [];
    for (const row of (json.data || []).map(normalizeRow)) {
      const mk = String(row.date).slice(0, 7);
      if (byMonth[mk]) byMonth[mk].push(row);
    }
    return byMonth;
  }, [authFetch]);

  const setMonth = useCallback((m) => { setExpandedDate(null); setCurrentMonth(m); }, []);

  // 月データ取得
  useEffect(() => {
    const k = ymKey(selectedYear, currentMonth);
    let cancelled = false;
    (async () => {
      try {
        setLoadingByMonth((p) => ({ ...p, [k]: true }));
        const txns = await fetchMonth(selectedYear, currentMonth);
        if (!cancelled) setTransactionsByMonth((prev) => ({ ...prev, [k]: txns }));
      } catch (e) {
        if (!cancelled) setTransactionsByMonth((prev) => ({ ...prev, [k]: prev[k] ?? [] }));
      } finally {
        if (!cancelled) setLoadingByMonth((p) => ({ ...p, [k]: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [selectedYear, currentMonth, fetchMonth]);

  // 年間データ取得
  useEffect(() => {
    if (view !== "annual") return;
    let cancelled = false;
    (async () => {
      try {
        setAnnualLoading(true);
        const byMonth = await fetchAnnual(selectedYear);
        if (!cancelled) setTransactionsByMonth((prev) => ({ ...prev, ...byMonth }));
      } catch (e) { console.error("❌ 年間取得失敗:", e); }
      finally { if (!cancelled) setAnnualLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [view, selectedYear, fetchAnnual]);

  // ===== 派生データ =====
  const monthKey = ymKey(selectedYear, currentMonth);
  const monthTxns = transactionsByMonth[monthKey] ?? [];
  const monthLoading = !!loadingByMonth[monthKey];

  const groupedByDate = useMemo(() => {
    const map = {};
    for (const tx of monthTxns) { if (!map[tx.date]) map[tx.date] = []; map[tx.date].push(tx); }
    return map;
  }, [monthTxns]);

  const sortedDates = useMemo(() => Object.keys(groupedByDate).sort().reverse(), [groupedByDate]);
  const monthTotal = useMemo(() => calcIncomeExpense(monthTxns), [monthTxns]);

  const annualTotal = useMemo(() => {
    let income = 0, expense = 0;
    for (let m = 1; m <= 12; m++) {
      const ie = calcIncomeExpense(transactionsByMonth[ymKey(selectedYear, m)] || []);
      income += ie.income; expense += ie.expense;
    }
    return { income, expense };
  }, [transactionsByMonth, selectedYear]);

  // ===== CRUD =====
  const addTransaction = async () => {
    const { date, category, amount, description } = newTransaction;
    console.log("📝 保存データ:", { date, category, amount, description });
    if (!date) { alert("⚠️ 日付を入力してください"); return; }
    if (!category) { alert("⚠️ カテゴリを選択してください"); return; }
    const numAmount = Number(amount);
    if (!amount || isNaN(numAmount) || numAmount <= 0) { alert("⚠️ 金額を正しく入力してください"); return; }

    try {
      const payload = { date, category, amount: numAmount, memo: description || "" };
      const res = await authFetch(`${BACKEND_URL}/api/transactions`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok || !json.success) { alert(json?.message || "保存に失敗しました"); return; }

      const saved = json.data || {};
      const normalized = normalizeRow({
        ...saved, id: saved.id ?? Date.now(), date: saved.date ?? payload.date,
        category: saved.category ?? payload.category, amount: saved.amount ?? payload.amount,
        memo: saved.memo ?? payload.memo,
      });
      const { year, month, monthKey: mk } = getYearMonthFromDate(normalized.date);
      if (year !== selectedYear) setSelectedYear(year);
      if (month !== currentMonth) setCurrentMonth(month);
      setTransactionsByMonth((prev) => ({ ...prev, [mk]: [normalized, ...(prev[mk] || [])] }));
      try { const txns = await fetchMonth(year, month); setTransactionsByMonth((prev) => ({ ...prev, [mk]: txns })); }
      catch (e) { console.warn("⚠️ 再取得失敗:", e); }
      setNewTransaction({ date: new Date().toISOString().split("T")[0], category: "sales", amount: "", description: "" });
      setShowAddForm(false); setOcrResult(null);
    } catch (e) { console.error("❌ 保存例外:", e); alert("保存に失敗しました"); }
  };

  const deleteTransaction = async (transaction) => {
    if (!window.confirm("この取引を削除しますか？")) return;
    const { year, month, monthKey: mk } = getYearMonthFromDate(transaction.date);
    setTransactionsByMonth((prev) => ({ ...prev, [mk]: (prev[mk] || []).filter((x) => x.id !== transaction.id) }));
    try {
      await authFetch(`${BACKEND_URL}/api/transactions/${transaction.id}`, { method: "DELETE" });
      try { const txns = await fetchMonth(year, month); setTransactionsByMonth((prev) => ({ ...prev, [mk]: txns })); }
      catch (e) { console.warn("⚠️ 再取得失敗:", e); }
    } catch (e) { console.error("❌ 削除例外:", e); }
  };

  const updateTransaction = async () => {
    if (!editingTransaction) return;
    const { id, date, category, amount, memo } = editingTransaction;
    if (!date || !category || !amount) { alert("日付・カテゴリ・金額は必須です"); return; }
    try {
      const res = await authFetch(`${BACKEND_URL}/api/transactions/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date, category, amount: Number(amount), memo: memo || "" }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) { alert(json?.message || "更新に失敗しました"); return; }
      const { year, month, monthKey: mk } = getYearMonthFromDate(date);
      try {
        const txns = await fetchMonth(year, month);
        setTransactionsByMonth((prev) => ({ ...prev, [mk]: txns }));
      } catch (e) { console.warn("再取得失敗:", e); }
      setEditingTransaction(null);
    } catch (e) { console.error("更新エラー:", e); alert("更新に失敗しました"); }
  };

  // ===== OCR =====
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setOcrLoading(true);
    try {
      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result.split(",")[1]);
        reader.onerror = () => reject(new Error("ファイル読み込み失敗"));
        reader.readAsDataURL(file);
      });
      const response = await authFetch(`${BACKEND_URL}/api/ocr`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageData: base64Image, isPdf: file.type === "application/pdf" }),
      });
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
      const data = await response.json();
      if (!data.success) { alert("❌ OCR処理に失敗しました"); return; }
      const ocrData = data.data;
      const catKey = normalizeCatToKey(ocrData.category);
      setNewTransaction({
        date: ocrData.date || new Date().toISOString().split("T")[0],
        category: catKey || "other",
        amount: ocrData.amount > 0 ? String(ocrData.amount) : "",
        description: ocrData.description || "",
      });
      setShowAddForm(true);
      setOcrResult({ ...ocrData, category: catKey, categoryLabel: KEY_TO_JP[catKey] || catKey });
    } catch (error) { console.error("OCR Error:", error); alert(`❌ OCRエラー: ${error.message}`); }
    finally { setOcrLoading(false); }
  };

  // ===== ヘルパー =====
  const isDiffPositive = (inc, exp) => inc - exp >= 0;

  // ===== CSVエクスポート =====

  /** 月次の取引一覧CSV */
  const exportMonthCsv = () => {
    if (monthTxns.length === 0) { alert("エクスポートするデータがありません"); return; }
    const header = "日付,カテゴリ,収支区分,金額,備考";
    const rows = monthTxns
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((tx) => [
        tx.date,
        csvEscape(KEY_TO_JP[tx.category] || tx.category),
        isIncomeCategory(tx.category) ? "収入" : "支出",
        tx.amount,
        csvEscape(tx.memo),
      ].join(","));
    const csv = [header, ...rows].join("\n");
    downloadCsv(`取引一覧_${selectedYear}年${currentMonth}月.csv`, csv);
  };

  /** 年間の取引一覧CSV */
  const exportAnnualCsv = () => {
    const allTxns = [];
    for (let m = 1; m <= 12; m++) {
      const txns = transactionsByMonth[ymKey(selectedYear, m)] || [];
      allTxns.push(...txns);
    }
    if (allTxns.length === 0) { alert("エクスポートするデータがありません"); return; }
    const header = "日付,カテゴリ,収支区分,金額,備考";
    const rows = allTxns
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((tx) => [
        tx.date,
        csvEscape(KEY_TO_JP[tx.category] || tx.category),
        isIncomeCategory(tx.category) ? "収入" : "支出",
        tx.amount,
        csvEscape(tx.memo),
      ].join(","));
    const csv = [header, ...rows].join("\n");
    downloadCsv(`取引一覧_${selectedYear}年.csv`, csv);
  };

  /** 確定申告フォーマット（科目別集計）CSV */
  const exportTaxCsv = () => {
    const header = "科目,金額";
    const rows = TAX_ITEMS.map((item) => {
      let total = 0;
      for (let m = 1; m <= 12; m++) {
        total += (transactionsByMonth[ymKey(selectedYear, m)] || [])
          .filter((x) => x.category === item.key)
          .reduce((sum, x) => sum + Number(x.amount || 0), 0);
      }
      return `${csvEscape(item.label)},${total}`;
    });

    // 収入合計・支出合計・差引も追加
    const incomeTotal = annualTotal.income;
    const expenseTotal = annualTotal.expense;
    rows.push("");
    rows.push(`収入合計,${incomeTotal}`);
    rows.push(`支出合計,${expenseTotal}`);
    rows.push(`差引金額,${incomeTotal - expenseTotal}`);

    const csv = [header, ...rows].join("\n");
    downloadCsv(`確定申告_${selectedYear}年.csv`, csv);
  };

  // ===== 描画 =====
  if (showProfile) {
    return <ProfilePage onBack={() => setShowProfile(false)} />;
  }

  if (showContact) {
    return <ContactPage onBack={() => setShowContact(false)} />;
  }
  return (
    <div className="min-h-screen bg-page text-color font-sans transition-colors duration-300">

      {/* ===== ヘッダー ===== */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b border-theme"
        style={{ backgroundColor: "var(--color-bg)", opacity: 0.97 }}>
        <div className="max-w-[1200px] mx-auto px-5 py-2">
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center gap-1.5">
              <div className="p-1.5 rounded-lg flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, var(--color-accent), var(--color-accent-soft))" }}>
                <Wallet size={18} color="#fff" />
              </div>
              <span className="text-base font-bold tracking-tight text-color">ポケ帳</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={toggleTheme} className="btn-theme-toggle"
                title={theme === "dark" ? "ライトモードに切替" : "ダークモードに切替"}>
                {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
              </button>
              <button onClick={() => setShowProfile(true)} className="btn-theme-toggle" title="基本情報">
                <Wallet size={17} />
              </button>
              <button onClick={() => setShowContact(true)} className="btn-theme-toggle" title="お問い合わせ">
                <Send size={17} />
              </button>
              <button onClick={() => signOut(auth)} className="btn-theme-toggle" title="ログアウト">
                <LogOut size={17} />
              </button>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="flex gap-0.5 p-1 rounded-xl border border-theme bg-soft">
              {[
                { id: "daily",   label: "毎日入力", icon: Calendar },
                { id: "monthly", label: "月次",     icon: BarChart3 },
                { id: "annual",  label: "年間",     icon: TrendingUp },
              ].map((tab) => (
                <button key={tab.id}
                  onClick={() => setView(tab.id)}
                  className={`tab-btn ${view === tab.id ? "tab-btn-active" : "tab-btn-inactive"}`}>
                  <tab.icon size={15} />
                  <span className="hidden md:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* ===== メイン ===== */}
      <main className="max-w-[1200px] mx-auto px-5 py-7 md:px-6">

        {/* ========== 毎日入力 ========== */}
        {view === "daily" && (
          <div className="grid grid-cols-1 md:grid-cols-[380px_1fr] gap-6 animate-fade-in">

            {/* 左: 入力パネル */}
            <div className="flex flex-col gap-4">
              <div className="card">
                <label className="field-label">年</label>
                <select value={selectedYear} className="select-field mb-3"
                  onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}>
                  {Array.from({ length: 11 }, (_, i) => {
                    const year = new Date().getFullYear() - 5 + i;
                    return <option key={year} value={year}>{year}年</option>;
                  })}
                </select>

                <label className="field-label">月を選択</label>
                <select value={currentMonth} className="select-field mb-4"
                  onChange={(e) => setMonth(parseInt(e.target.value, 10))}>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}月</option>
                  ))}
                </select>

                {monthLoading && (
                  <div className="flex items-center gap-1.5 text-muted text-xs mb-3">
                    <Loader size={12} className="animate-spin" /> 読み込み中...
                  </div>
                )}

                <button onClick={() => setShowAddForm(!showAddForm)} className="btn-primary flex items-center justify-center gap-1.5">
                  <Plus size={17} /> 取引を追加
                </button>

                {/* 入力フォーム */}
                {showAddForm && (
                  <div className="mt-5 pt-5 border-t border-theme animate-slide-down overflow-hidden">
                    <label className="field-label">日付</label>
                    <input type="date" value={newTransaction.date} className="input-field mb-3"
                      onChange={(e) => setNewTransaction((p) => ({ ...p, date: e.target.value }))} />

                    <label className="field-label">カテゴリ</label>
                    <select value={newTransaction.category} className="select-field mb-3"
                      onChange={(e) => setNewTransaction((p) => ({ ...p, category: e.target.value }))}>
                      {Object.entries(categories).map(([key, v]) => (
                        <option key={key} value={key}>{v.label}</option>
                      ))}
                    </select>

                    <label className="field-label">金額（円）</label>
                    <input type="number" value={newTransaction.amount} className="input-field font-yen mb-3" placeholder="0"
                      onChange={(e) => setNewTransaction((p) => ({ ...p, amount: e.target.value }))} />

                    <label className="field-label">備考</label>
                    <input type="text" value={newTransaction.description} className="input-field mb-4" placeholder="メモ"
                      onChange={(e) => setNewTransaction((p) => ({ ...p, description: e.target.value }))} />

                    {/* OCR */}
                    <label className="field-label">OCR読み込み</label>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <button onClick={() => document.getElementById("camera-input").click()}
                        disabled={ocrLoading}
                        className="btn-primary flex items-center justify-center gap-1.5 text-sm py-2.5"
                        style={{ background: "linear-gradient(135deg, #8b5cf6, #6d28d9)", opacity: ocrLoading ? 0.7 : 1 }}>
                        {ocrLoading ? <Loader size={14} className="animate-spin" /> : <Camera size={14} />}
                        カメラで撮影
                      </button>
                      <button onClick={() => document.getElementById("image-input").click()}
                        disabled={ocrLoading}
                        className="btn-primary flex items-center justify-center gap-1.5 text-sm py-2.5"
                        style={{ background: "linear-gradient(135deg, #8b5cf6, #6d28d9)", opacity: ocrLoading ? 0.7 : 1 }}>
                        {ocrLoading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
                        ファイル選択
                      </button>
                    </div>
                    <input id="camera-input" type="file" accept="image/*" capture="environment" onChange={handleImageUpload} className="hidden" />
                    <input id="image-input" type="file" accept="image/*,application/pdf" onChange={handleImageUpload} className="hidden" />

                    {/* OCR結果 */}
                    {ocrResult && (
                      <div className={`p-3 rounded-xl mb-3 text-xs border ${
                        ocrResult.confidence === "high"
                          ? "bg-[var(--color-income-bg)] border-[rgba(52,211,153,0.2)]"
                          : ocrResult.confidence === "medium"
                          ? "bg-[rgba(251,191,36,0.08)] border-[rgba(251,191,36,0.2)]"
                          : "bg-[var(--color-expense-bg)] border-[rgba(248,113,113,0.2)]"
                      }`}>
                        <div className="font-semibold mb-1.5">
                          {ocrResult.confidence === "high" ? "✅" : ocrResult.confidence === "medium" ? "⚠️" : "❓"}
                          {` OCR完了（信頼度: ${ocrResult.confidence === "high" ? "高" : ocrResult.confidence === "medium" ? "中" : "低"}）`}
                        </div>
                        <div className="text-soft leading-relaxed">
                          日付: {ocrResult.date}<br />
                          金額: {formatYen(ocrResult.amount)}<br />
                          カテゴリ: {ocrResult.categoryLabel}
                          {ocrResult.description && <><br />備考: {ocrResult.description}</>}
                        </div>
                        <div className="mt-1.5 text-muted text-[11px]">📝 確認・修正してから「保存」を押してください</div>
                      </div>
                    )}

                    <button onClick={addTransaction} className="btn-primary"
                      style={{ background: "linear-gradient(135deg, var(--color-income), #059669)" }}>
                      保存
                    </button>
                  </div>
                )}
              </div>

              {/* 月サマリー */}
              <div className="card">
                <h3 className="text-xs font-semibold text-muted tracking-widest mb-4">{currentMonth}月 サマリー</h3>

                {[
                  { label: "収入", value: monthTotal.income, cls: "text-income", Icon: TrendingUp },
                  { label: "支出", value: monthTotal.expense, cls: "text-expense", Icon: TrendingDown },
                ].map((row) => (
                  <div key={row.label} className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <row.Icon size={15} style={{ color: `var(--color-${row.label === "収入" ? "income" : "expense"})` }} />
                      <span className="text-soft text-sm">{row.label}</span>
                    </div>
                    <span className={`${row.cls} font-bold text-[15px] font-yen`}>{formatYen(row.value)}</span>
                  </div>
                ))}

                <div className="border-t border-theme pt-3 flex justify-between items-center">
                  <span className="text-soft text-sm font-semibold">差引</span>
                  <span className={`font-bold text-lg font-yen ${isDiffPositive(monthTotal.income, monthTotal.expense) ? "text-income" : "text-expense"}`}>
                    {formatYen(monthTotal.income - monthTotal.expense)}
                  </span>
                </div>
              </div>
            </div>

            {/* 右: 取引一覧 */}
            <div>
              {sortedDates.length === 0 ? (
                <div className="card text-center py-16">
                  <Calendar size={44} className="mx-auto mb-4 opacity-25 text-muted" />
                  <p className="text-muted text-[15px]">取引データがまだありません</p>
                  <p className="text-muted text-sm mt-1">「取引を追加」から入力してください</p>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {sortedDates.map((date) => (
                    <div key={date} className="animate-fade-in">
                      <div
                        onClick={() => setExpandedDate(expandedDate === date ? null : date)}
                        className={`date-header ${expandedDate === date ? "date-header-expanded" : ""}`}>
                        <div className="flex items-center gap-2.5">
                          <Calendar size={14} className="text-muted" />
                          <span className="font-semibold text-sm">
                            {new Date(date).toLocaleDateString("ja-JP", { month: "numeric", day: "numeric", weekday: "short" })}
                          </span>
                          <span className="badge">{groupedByDate[date].length}件</span>
                        </div>
                        {expandedDate === date
                          ? <ChevronDown size={15} className="text-muted" />
                          : <ChevronRight size={15} className="text-muted" />}
                      </div>

                      {expandedDate === date && (
                        <div className="bg-soft border border-t-0 rounded-b-xl p-1.5 animate-slide-down overflow-hidden"
                          style={{ borderColor: "rgba(108,140,255,0.2)" }}>
                          {groupedByDate[date].map((tx) => (
                            <div key={tx.id} className="transaction-item">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold truncate">{KEY_TO_JP[tx.category] || tx.category}</div>
                                {tx.memo && <div className="text-xs text-muted truncate">{tx.memo}</div>}
                              </div>
                              <div className="flex items-center gap-2.5 ml-3 shrink-0">
                                <span className={`font-bold text-sm font-yen ${isIncomeCategory(tx.category) ? "text-income" : "text-color"}`}>
                                  {formatYen(tx.amount)}
                                </span>
                                <button onClick={() => setEditingTransaction({
                                  id: tx.id, date: tx.date, category: tx.category,
                                  amount: String(tx.amount), memo: tx.memo || "",
                                })} className="btn-delete" title="編集" style={{ color: "var(--color-accent)" }}>
                                  <Calendar size={13} />
                                </button>
                                <button onClick={() => deleteTransaction(tx)} className="btn-delete">
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== 月次まとめ ========== */}
        {view === "monthly" && (
          <div className="animate-fade-in">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                <button key={month}
                  onClick={() => setMonth(month)}
                  className={`month-btn ${currentMonth === month ? "month-btn-active" : "month-btn-inactive"}`}>
                  {month}月
                </button>
              ))}
            </div>

            <div className="card">
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                {selectedYear}年{currentMonth}月
                {monthLoading && <Loader size={14} className="animate-spin text-muted" />}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                {[
                  { label: "総収入", value: monthTotal.income, cls: "summary-card-income", textCls: "text-income", Icon: TrendingUp },
                  { label: "総支出", value: monthTotal.expense, cls: "summary-card-expense", textCls: "text-expense", Icon: TrendingDown },
                  { label: "差引金額", value: monthTotal.income - monthTotal.expense,
                    cls: "summary-card-balance",
                    textCls: isDiffPositive(monthTotal.income, monthTotal.expense) ? "text-income" : "text-expense",
                    Icon: Wallet },
                ].map((item) => (
                  <div key={item.label} className={`summary-card ${item.cls}`}>
                    <div className="flex items-center gap-1.5 mb-2.5">
                      <item.Icon size={15} />
                      <span className="text-xs font-semibold text-soft">{item.label}</span>
                    </div>
                    <span className={`text-2xl font-bold font-yen ${item.textCls}`}>{formatYen(item.value)}</span>
                  </div>
                ))}
              </div>

              {/* CSVエクスポート */}
              <div className="mt-5 pt-5 border-t border-theme">
                <button onClick={exportMonthCsv}
                  className="flex items-center gap-2 text-sm font-medium text-accent hover:opacity-80 transition-opacity cursor-pointer bg-transparent border-none">
                  <Download size={15} /> {currentMonth}月の取引一覧をCSVエクスポート
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========== 年間累計 ========== */}
        {view === "annual" && (
          <div className="animate-fade-in">
            <div className="flex items-center gap-4 flex-wrap mb-6">
              <label className="field-label !mb-0">年を選択</label>
              <input type="number" value={selectedYear} className="input-field font-yen !w-28 !mb-0"
                onChange={(e) => { const v = parseInt(e.target.value, 10); if (v > 0) setSelectedYear(v); }} />
              {annualLoading && (
                <span className="text-muted text-xs flex items-center gap-1">
                  <Loader size={12} className="animate-spin" /> 取得中...
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 mb-6">
              {[
                { label: "年間総収入", value: annualTotal.income, cls: "summary-card-income", textCls: "text-income", Icon: TrendingUp },
                { label: "年間総支出", value: annualTotal.expense, cls: "summary-card-expense", textCls: "text-expense", Icon: TrendingDown },
                { label: "年間差引", value: annualTotal.income - annualTotal.expense,
                  cls: "summary-card-balance",
                  textCls: isDiffPositive(annualTotal.income, annualTotal.expense) ? "text-income" : "text-expense",
                  Icon: Wallet },
              ].map((item) => (
                <div key={item.label} className={`summary-card ${item.cls}`}>
                  <div className="flex items-center gap-1.5 mb-2.5">
                    <item.Icon size={15} />
                    <span className="text-xs font-semibold text-soft">{item.label}</span>
                  </div>
                  <span className={`text-2xl md:text-[26px] font-bold font-yen ${item.textCls}`}>{formatYen(item.value)}</span>
                </div>
              ))}
            </div>

            <div className="card">
              <h3 className="text-base font-bold mb-5">確定申告フォーマット</h3>
              <div className="overflow-x-auto">
                <table className="tax-table">
                  <thead>
                    <tr><th>科目</th><th>金額（円）</th></tr>
                  </thead>
                  <tbody>
                    {TAX_ITEMS.map((item) => {
                      let total = 0;
                      for (let m = 1; m <= 12; m++) {
                        total += (transactionsByMonth[ymKey(selectedYear, m)] || [])
                          .filter((x) => x.category === item.key)
                          .reduce((sum, x) => sum + Number(x.amount || 0), 0);
                      }
                      return (
                        <tr key={item.key}>
                          <td>{item.label}</td>
                          <td className={`text-right font-semibold font-yen ${
                            total > 0
                              ? INCOME_KEYS.has(item.key) ? "text-income" : "text-color"
                              : "text-muted"
                          }`}>{formatYen(total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-muted text-xs">※年間合計は全月のデータを一括取得して集計しています。</p>

              {/* CSVエクスポート */}
              <div className="mt-5 pt-5 border-t border-theme flex flex-col md:flex-row gap-3">
                <button onClick={exportAnnualCsv}
                  className="flex items-center gap-2 text-sm font-medium text-accent hover:opacity-80 transition-opacity cursor-pointer bg-transparent border-none">
                  <Download size={15} /> {selectedYear}年の取引一覧CSV
                </button>
                <button onClick={exportTaxCsv}
                  className="flex items-center gap-2 text-sm font-medium text-income hover:opacity-80 transition-opacity cursor-pointer bg-transparent border-none">
                  <Download size={15} /> 確定申告フォーマットCSV
                </button>
                <button
                  onClick={async () => {
                    try {
                      const token = await getToken();
                      const res = await fetch(
                        `${BACKEND_URL}/api/tax-report/white?year=${selectedYear}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      if (!res.ok) throw new Error("PDF生成に失敗しました");
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `収支内訳書_${selectedYear}年.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      alert("PDF生成に失敗しました: " + e.message);
                    }
                  }}
                  className="flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-opacity cursor-pointer bg-transparent border-none"
                  style={{ color: "var(--color-accent)" }}>
                  <Download size={15} /> 収支内訳書PDF（白色申告）
                </button>
                <button
                  onClick={async () => {
                    try {
                      const token = await getToken();
                      const res = await fetch(
                        `${BACKEND_URL}/api/tax-report/blue?year=${selectedYear}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      if (!res.ok) throw new Error("PDF生成に失敗しました");
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `青色申告決算書_${selectedYear}年.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      alert("PDF生成に失敗しました: " + e.message);
                    }
                  }}
                  className="flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-opacity cursor-pointer bg-transparent border-none"
                  style={{ color: "var(--color-income)" }}>
                  <Download size={15} /> 青色申告決算書PDF
                </button>
                <button
                  onClick={async () => {
                    try {
                      const token = await getToken();
                      const type = window.confirm("青色申告ですか？\n\nOK → 青色申告\nキャンセル → 白色申告") ? "blue" : "white";
                      const res = await fetch(
                        `${BACKEND_URL}/api/tax-report/guide?year=${selectedYear}&type=${type}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      if (!res.ok) throw new Error("PDF生成に失敗しました");
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `確定申告ガイド_${selectedYear}年_${type === "blue" ? "青色" : "白色"}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      alert("PDF生成に失敗しました: " + e.message);
                    }
                  }}
                  className="flex items-center gap-2 text-sm font-medium hover:opacity-80 transition-opacity cursor-pointer bg-transparent border-none"
                  style={{ color: "#f59e0b" }}>
                  <Download size={15} /> 確定申告 入力ガイドPDF
                </button>
              </div>
            </div>
          </div>
        )}

        {editingTransaction && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.6)", zIndex: 100,
            display: "flex", alignItems: "center", justifyContent: "center",
          }} onClick={() => setEditingTransaction(null)}>
            <div className="card" style={{ width: "90%", maxWidth: 400 }}
              onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold mb-4">取引を編集</h3>
              <label className="field-label">日付</label>
              <input type="date" value={editingTransaction.date} className="input-field mb-3"
                onChange={(e) => setEditingTransaction((p) => ({ ...p, date: e.target.value }))} />
              <label className="field-label">カテゴリ</label>
              <select value={editingTransaction.category} className="select-field mb-3"
                onChange={(e) => setEditingTransaction((p) => ({ ...p, category: e.target.value }))}>
                {Object.entries(categories).map(([key, v]) => (
                  <option key={key} value={key}>{v.label}</option>
                ))}
              </select>
              <label className="field-label">金額（円）</label>
              <input type="number" value={editingTransaction.amount} className="input-field font-yen mb-3"
                onChange={(e) => setEditingTransaction((p) => ({ ...p, amount: e.target.value }))} />
              <label className="field-label">備考</label>
              <input type="text" value={editingTransaction.memo} className="input-field mb-4"
                onChange={(e) => setEditingTransaction((p) => ({ ...p, memo: e.target.value }))} />
              <div className="flex gap-2">
                <button onClick={updateTransaction} className="btn-primary flex-1"
                  style={{ background: "linear-gradient(135deg, var(--color-income), #059669)" }}>
                  保存
                </button>
                <button onClick={() => setEditingTransaction(null)}
                  className="btn-primary flex-1" style={{ background: "var(--color-bg-soft)" }}>
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        )}
        
      </main>
    </div>
  );
};

export default AccountingApp;